import React, { useMemo, useState } from "react";
import Field from "./shared/Field";
import HeroImageUploader from "./shared/HeroImageUploader";
import StatsEditor from "./shared/StatsEditor";
import ConfirmDeleteModal from "../../../../components/GeneralComponents/ConfirmDeleteModal";

function normalizeHeroTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{toggleLabel || "Toggle"}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active
              ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function HeroSectionEditor({ config, template, onChange, uploadImage, t }) {
  const [showRemoveImageModal, setShowRemoveImageModal] = useState(false);
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeHeroTemplate(template), [template]);
  const dir = cfg.direction === "rtl" ? "rtl" : "ltr";

  const ui = useMemo(() => {
    return {
      template1: { showBadge: true, showTag: false, showStats: false },
      template2: { showBadge: false, showTag: true, showStats: false },
      template3: { showBadge: false, showTag: false, showStats: true },
    }[tplKey] || { showBadge: true, showTag: false, showStats: false };
  }, [tplKey]);

  return (
    <div className="space-y-4">
      {/* Layout */}
      <Group
        title={t?.("heroEditor.group.layout", "Layout")}
        subtitle={t?.("heroEditor.group.layoutHelp", "Direction for this hero")}
        toggleLabel={t?.("heroEditor.group.layoutToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t?.("heroEditor.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => onChange("direction", v)}
              options={[
                { value: "ltr", label: t?.("heroEditor.direction.ltr", "LTR") },
                { value: "rtl", label: t?.("heroEditor.direction.rtl", "RTL") },
              ]}
            />

            <div className="text-xs text-gray-500 mt-2">
              {t?.("heroEditor.layoutDirectionHelp", "Use RTL for Arabic and Hebrew, LTR for English.")}
            </div>
          </div>
        </div>
      </Group>

      {/* Text */}
      <Group
        title={t?.("heroEditor.group.text", "Text")}
        subtitle={t?.("heroEditor.group.textHelp", "Main title and subtitle")}
        toggleLabel={t?.("heroEditor.group.textToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <Field
            label={t?.("heroEditor.title", "Title")}
            value={cfg.title}
            onChange={(v) => onChange("title", v)}
          />

          <Field
            label={t?.("heroEditor.subtitle", "Subtitle")}
            type="textarea"
            value={cfg.subtitle}
            onChange={(v) => onChange("subtitle", v)}
          />
        </div>
      </Group>

      {/* Buttons */}
      <Group
        title={t?.("heroEditor.group.buttons", "Buttons")}
        subtitle={t?.("heroEditor.group.buttonsHelp", "Button labels and links")}
        toggleLabel={t?.("heroEditor.group.buttonsToggle", "Toggle")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t?.("heroEditor.primaryButtonText", "Primary Button Text")}
              value={cfg.buttonText}
              onChange={(v) => onChange("buttonText", v)}
            />
            <Field
              label={t?.("heroEditor.primaryButtonLink", "Primary Button Link")}
              value={cfg.buttonLink}
              onChange={(v) => onChange("buttonLink", v)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t?.("heroEditor.secondaryButtonText", "Secondary Button Text")}
              value={cfg.secondaryButtonText}
              onChange={(v) => onChange("secondaryButtonText", v)}
            />
            <Field
              label={t?.("heroEditor.secondaryButtonLink", "Secondary Button Link")}
              value={cfg.secondaryButtonLink}
              onChange={(v) => onChange("secondaryButtonLink", v)}
            />
          </div>
        </div>
      </Group>

      {/* Media */}
      <Group
        title={t?.("heroEditor.group.media", "Media")}
        subtitle={t?.("heroEditor.group.mediaHelp", "Hero image and alt text")}
        toggleLabel={t?.("heroEditor.group.mediaToggle", "Toggle")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-gray-700">
              {t?.("heroEditor.image", "Image")}
            </div>

            {cfg.image ? (
              <button
                type="button"
                onClick={() => setShowRemoveImageModal(true)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                {t?.("heroEditor.removeImage", "Remove image")}
              </button>
            ) : null}
          </div>

          {cfg.image ? (
            <img src={cfg.image} alt="" className="w-full rounded-xl border border-gray-200" />
          ) : (
            <div className="w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              {t?.("heroEditor.noImage", "No image selected yet")}
            </div>
          )}

          <HeroImageUploader
            kind="hero"
            uploadImage={uploadImage}
            onUploaded={(url) => onChange("image", url)}
            t={t}
          />

          <Field
            label={t?.("heroEditor.altText", "Alt Text")}
            value={cfg.imageAlt}
            onChange={(v) => onChange("imageAlt", v)}
          />

          <details className="rounded-xl border border-gray-200 bg-white">
            <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">
                {t?.("heroEditor.advanced", "Advanced")}
              </div>
              <div className="text-xs text-gray-400">
                {t?.("heroEditor.advancedToggle", "Toggle")}
              </div>
            </summary>
            <div className="px-4 pb-4">
              <Field
                label={t?.("heroEditor.imageUrl", "Image URL")}
                type="url"
                value={cfg.image}
                onChange={(v) => onChange("image", v)}
                placeholder={t?.("heroEditor.imagePlaceholder", "Paste image URL")}
              />
            </div>
          </details>
        </div>
      </Group>

      {/* Template extras */}
      <Group
        title={t?.("heroEditor.group.extras", "Template Extras")}
        subtitle={t?.("heroEditor.group.extrasHelp", "Only shown for your selected template")}
        toggleLabel={t?.("heroEditor.group.extrasToggle", "Toggle")}
        defaultOpen={false}
      >
        {ui.showBadge && (
          <div className="space-y-4">
            <Field
              label={t?.("heroEditor.badgeTitle", "Badge Title")}
              value={cfg.badgeTitle}
              onChange={(v) => onChange("badgeTitle", v)}
            />
            <Field
              label={t?.("heroEditor.badgeSubtitle", "Badge Subtitle")}
              value={cfg.badgeSubtitle}
              onChange={(v) => onChange("badgeSubtitle", v)}
            />
          </div>
        )}

        {ui.showTag && (
          <div className="space-y-4">
            <Field
              label={t?.("heroEditor.tagEmoji", "Tag Emoji")}
              value={cfg.tagEmoji}
              onChange={(v) => onChange("tagEmoji", v)}
            />
            <Field
              label={t?.("heroEditor.tagText", "Tag Text")}
              value={cfg.tagText}
              onChange={(v) => onChange("tagText", v)}
            />
          </div>
        )}

        {ui.showStats && (
          <div className="space-y-4">
            <div className="text-sm font-medium text-gray-700">
              {t?.("heroEditor.stats.title", "Stats")}
            </div>
            <StatsEditor
              value={cfg.stats}
              onChange={(next) => onChange("stats", next)}
              t={t}
            />
          </div>
        )}

        {!ui.showBadge && !ui.showTag && !ui.showStats ? (
          <div className="text-sm text-gray-500">
            {t?.("heroEditor.noExtras", "No extra fields for this template.")}
          </div>
        ) : null}
      </Group>

      <ConfirmDeleteModal
        open={showRemoveImageModal}
        t={t}
        title={t?.("heroEditor.removeImageTitle", "Remove image?")}
        message={t?.(
          "heroEditor.removeImageMessage",
          "Are you sure you want to remove this image?"
        )}
        confirmText={t?.("heroEditor.removeImageConfirm", "Remove")}
        cancelText={t?.("heroEditor.removeImageCancel", "Cancel")}
        onClose={() => setShowRemoveImageModal(false)}
        onConfirm={() => {
          onChange("image", "");
          setShowRemoveImageModal(false);
        }}
      />
    </div>
  );
}